import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Search, Loader2 } from "lucide-react";

import { STATUS_OPTIONS } from "./components/messageStatus";
import MessageRow from "./components/MessageRow";
import ContactDetail from "./components/ContactDetail";
import useMessageFilters from "./hooks/useMessageFilters";

function TableState({ loading, isEmpty, children }) {
    if (loading) {
        return (
            <div className="p-12 text-center text-[#6F6B63] flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading messages…
            </div>
        );
    }
    if (isEmpty) {
        return (
            <div className="p-12 text-center text-[#6F6B63]" data-testid="messages-empty">
                <Inbox className="h-8 w-8 mx-auto mb-3 text-[#8F8B83]" />
                No messages match your filters.
            </div>
        );
    }
    return children;
}

export default function AdminMessages() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [selected, setSelected] = useState(null);
    const location = useLocation();

    useEffect(() => { setSelected(null); }, [location.pathname]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") setSelected(null); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/contact");
                if (!cancelled) setRows(data);
            } catch (e) {
                if (!cancelled) setError(formatApiError(e.response?.data?.detail) || e.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const updateStatus = async (id, status) => {
        try {
            const { data } = await api.patch(`/contact/${id}`, { status });
            setRows((rs) => rs.map((r) => (r.id === id ? data : r)));
            setSelected((s) => (s && s.id === id ? data : s));
            toast.success(`Marked as ${status}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const filtered = useMessageFilters(rows, q, statusFilter);

    return (
        <div className="space-y-6" data-testid="admin-messages-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">Admin</p>
                    <h1 className="mt-2 font-[Outfit] text-3xl font-semibold tracking-tight">Messages</h1>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8F8B83]" />
                        <input
                            placeholder="Search by name, email, subject…"
                            className="rtc-input pl-10 w-72 max-w-[60vw]"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="messages-search"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="rtc-input w-40"
                        data-testid="messages-status-filter"
                    >
                        <option value="all">All statuses</option>
                        {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>
            </div>

            {error && (
                <div className="rounded-xl bg-[#fdf6e8] border border-[#C8A24A]/30 text-[#8a6a1f] px-4 py-3 text-[13.5px]">
                    {error}
                </div>
            )}

            <div className="bg-white rounded-2xl border border-[#E5DFD3] overflow-hidden">
                <TableState loading={loading} isEmpty={filtered.length === 0}>
                    <ul className="divide-y divide-[#E5DFD3]">
                        {filtered.map((r) => (
                            <MessageRow key={r.id} row={r} onSelect={setSelected} />
                        ))}
                    </ul>
                </TableState>
            </div>

            <ContactDetail row={selected} onClose={() => setSelected(null)} onUpdate={updateStatus} />
        </div>
    );
}
